// public/js/features/user-analytics/user-analytics-page.js
// Superadmin-only "User activity" page: live presence + GA-style analytics.
// Gated on the literal 'superadmin' group (NOT admin). See docs/user-analytics.md.
import { loadLive, loadOverview, loadUsersSummary, loadUserHistory } from './api.js';
import { getCurrentUser } from '../../modules/rbac.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const LIVE_POLL_MS = 12000;

const PAGE_LABELS = {
  '#map': 'Map', '#assets': 'Assets', '#devices': 'Devices', '#users': 'Users',
  '#tenants': 'Tenants', '#settings': 'Settings', '#alerts': 'Alerts', '#feedback': 'Feedback',
  '#reports': 'Reports', '#subscription': 'Subscription', '#distribution': 'Device distribution',
  '#whats-new': "What's new", '#user-activity': 'User activity',
};
const pageLabel = (h) => PAGE_LABELS[h] || (h ? h.replace(/^#/, '').replace(/\b\w/g, c => c.toUpperCase()) : '—');
const nameOf = (u) => (u.email ? u.email.split('@')[0] : (u.name || u.userId || '—'));

function fmtDur(ms) {
  const s = Math.round((ms || 0) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), r = s % 60;
  if (m < 60) return `${m}m ${r}s`;
  const h = Math.floor(m / 60); return `${h}h ${m % 60}m`;
}

let liveTimer = null;
let charts = [];
function clearTimers() { if (liveTimer) clearInterval(liveTimer); liveTimer = null; }
function destroyCharts() { charts.forEach(c => { try { c.destroy(); } catch {} }); charts = []; }

export async function initUserAnalyticsPage(container) {
  clearTimers(); destroyCharts();
  if (!(getCurrentUser().groups || []).includes('superadmin')) {
    container.innerHTML = `<div class="alert alert-danger m-3">Access denied — superadmin only.</div>`;
    return;
  }
  // Stop the live poll when the user navigates away from this page.
  window.addEventListener('hashchange', () => { if ((location.hash || '') !== '#user-activity') { clearTimers(); destroyCharts(); } }, { once: true });

  container.innerHTML = `
    <div class="container-fluid py-3">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0"><i class="fa-solid fa-chart-line me-2"></i>User activity</h1>
      </div>
      <ul class="nav nav-tabs mb-3" id="ua-tabs">
        <li class="nav-item"><button class="nav-link active" data-tab="live"><i class="fa-solid fa-satellite-dish me-1"></i>Live</button></li>
        <li class="nav-item"><button class="nav-link" data-tab="users"><i class="fa-solid fa-users me-1"></i>Users</button></li>
        <li class="nav-item"><button class="nav-link" data-tab="analytics"><i class="fa-solid fa-chart-column me-1"></i>Analytics</button></li>
      </ul>
      <div id="ua-body"></div>
    </div>`;

  let activeTab = 'live';
  const body = container.querySelector('#ua-body');
  const tabs = container.querySelector('#ua-tabs');
  tabs.querySelectorAll('[data-tab]').forEach(btn => btn.addEventListener('click', () => {
    activeTab = btn.dataset.tab;
    tabs.querySelectorAll('.nav-link').forEach(b => b.classList.toggle('active', b === btn));
    clearTimers(); destroyCharts();
    if (activeTab === 'live') renderLive(body);
    else if (activeTab === 'users') renderUsers(body);
    else renderAnalytics(body);
  }));

  renderLive(body);
}

// ---------------- LIVE ----------------
async function renderLive(body) {
  body.innerHTML = `<div id="ua-kpi" class="row row-cols-3 g-2 mb-3"></div>
    <div class="card shadow-sm"><div class="table-responsive">
      <table class="table table-hover align-middle mb-0">
        <thead class="table-light"><tr><th>User</th><th>Page</th><th>Device</th><th>State</th><th class="text-end">Session</th><th class="text-end">Last ping</th></tr></thead>
        <tbody id="ua-live-rows"><tr><td colspan="6" class="text-muted py-3"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</td></tr></tbody>
      </table></div></div>
    <div class="text-muted small mt-2">Auto-refreshing every ${LIVE_POLL_MS / 1000}s.</div>`;

  const paint = async () => {
    let data; try { data = await loadLive(); } catch { return; }
    const kpi = body.querySelector('#ua-kpi');
    const card = (label, val, cls) => `<div class="col"><div class="border rounded p-2 text-center"><div class="h4 mb-0 fw-bold ${cls}">${val}</div><div class="small text-muted">${label}</div></div></div>`;
    if (kpi) kpi.innerHTML = card('Online', data.counts.total, '') + card('Active', data.counts.active, 'text-success') + card('Idle', data.counts.idle, 'text-warning');
    const rows = body.querySelector('#ua-live-rows');
    if (!rows) return;
    rows.innerHTML = data.users.length ? data.users.map(u => `
      <tr class="ua-user-row" role="button" data-uid="${esc(u.userId)}" data-name="${esc(nameOf(u))}">
        <td class="fw-semibold">${esc(nameOf(u))}<span class="d-block text-muted small fw-normal">${esc(u.email || '')}</span></td>
        <td>${esc(pageLabel(u.page))}</td>
        <td><i class="fa-solid fa-${u.device === 'mobile' ? 'mobile-screen' : 'desktop'} text-secondary me-1"></i>${u.device}</td>
        <td>${u.active ? '<span class="badge text-bg-success">active</span>' : '<span class="badge text-bg-warning">idle</span>'}</td>
        <td class="text-end">${fmtDur(u.sessionMs)}</td>
        <td class="text-end text-muted small">${timeAgo(u.lastPingAt)}</td>
      </tr>`).join('') : `<tr><td colspan="6" class="text-center text-muted py-4">Nobody online right now.</td></tr>`;
    rows.querySelectorAll('.ua-user-row').forEach(r => r.addEventListener('click', () => openUserHistory(r.dataset.uid, r.dataset.name)));
  };
  await paint();
  clearTimers();
  liveTimer = setInterval(paint, LIVE_POLL_MS);
}

function timeAgo(iso) {
  const s = (Date.now() - (Date.parse(iso) || Date.now())) / 1000;
  if (s < 10) return 'now'; if (s < 60) return `${Math.floor(s)}s ago`;
  return `${Math.floor(s / 60)}m ago`;
}

// Richer relative time for the Users tab (minutes → hours → days).
function relAgo(iso) {
  const ms = Date.now() - (Date.parse(iso) || Date.now());
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7); if (w < 5) return `${w}w ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

// ---------------- USERS (recent + past) ----------------
async function renderUsers(body) {
  body.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="btn-group btn-group-sm" id="ua-urange">
        <button class="btn btn-outline-secondary" data-days="7">7 days</button>
        <button class="btn btn-primary" data-days="30">30 days</button>
        <button class="btn btn-outline-secondary" data-days="90">90 days</button>
        <button class="btn btn-outline-secondary" data-days="365">1 year</button>
      </div>
      <div id="ua-ucount" class="text-muted small"></div>
    </div>
    <div class="card shadow-sm"><div class="table-responsive">
      <table class="table table-hover align-middle mb-0">
        <thead class="table-light"><tr>
          <th>User</th><th>Last seen</th><th class="text-end">Pageviews</th>
          <th class="text-end">Time on app</th><th>Device</th>
        </tr></thead>
        <tbody id="ua-users-rows"><tr><td colspan="5" class="text-muted py-3"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</td></tr></tbody>
      </table></div></div>`;

  const rangeEl = body.querySelector('#ua-urange');
  const load = async (days) => {
    rangeEl.querySelectorAll('button').forEach(b => { const on = +b.dataset.days === days; b.classList.toggle('btn-primary', on); b.classList.toggle('btn-outline-secondary', !on); });
    const rows = body.querySelector('#ua-users-rows');
    let users; try { users = await loadUsersSummary(days); } catch { rows.innerHTML = '<tr><td colspan="5" class="text-danger">Failed to load.</td></tr>'; return; }
    body.querySelector('#ua-ucount').textContent = `${users.length} user${users.length === 1 ? '' : 's'} active in range`;
    rows.innerHTML = users.length ? users.map(u => `
      <tr class="ua-user-row" role="button" data-uid="${esc(u.userId)}" data-name="${esc(nameOf(u))}">
        <td class="fw-semibold">${esc(nameOf(u))}<span class="d-block text-muted small fw-normal">${esc(u.email || '')}</span></td>
        <td title="${u.lastAt ? new Date(u.lastAt).toLocaleString(navigator.language) : ''}">${relAgo(u.lastAt)}</td>
        <td class="text-end fw-semibold">${u.pageviews}</td>
        <td class="text-end text-muted">${fmtDur(u.totalMs)}</td>
        <td><i class="fa-solid fa-${u.device === 'mobile' ? 'mobile-screen' : 'desktop'} text-secondary me-1"></i>${u.device}</td>
      </tr>`).join('') : '<tr><td colspan="5" class="text-center text-muted py-4">No activity in this range.</td></tr>';
    rows.querySelectorAll('.ua-user-row').forEach(r => r.addEventListener('click', () => openUserHistory(r.dataset.uid, r.dataset.name)));
  };
  rangeEl.querySelectorAll('button').forEach(b => b.addEventListener('click', () => load(+b.dataset.days)));
  await load(30);
}

// ---------------- ANALYTICS ----------------
async function renderAnalytics(body) {
  body.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="btn-group btn-group-sm" id="ua-range">
        <button class="btn btn-outline-secondary" data-days="7">7 days</button>
        <button class="btn btn-primary" data-days="30">30 days</button>
        <button class="btn btn-outline-secondary" data-days="90">90 days</button>
      </div>
      <div id="ua-totals" class="text-muted small"></div>
    </div>
    <div class="row g-3">
      <div class="col-12 col-lg-6"><div class="card shadow-sm h-100"><div class="card-header py-2 small fw-semibold">Active users / day</div><div class="card-body"><canvas id="ua-users"></canvas></div></div></div>
      <div class="col-12 col-lg-6"><div class="card shadow-sm h-100"><div class="card-header py-2 small fw-semibold">Pageviews / day</div><div class="card-body"><canvas id="ua-views"></canvas></div></div></div>
      <div class="col-12 col-lg-8"><div class="card shadow-sm h-100"><div class="card-header py-2 small fw-semibold">Top pages</div><div class="card-body"><canvas id="ua-pages"></canvas></div></div></div>
      <div class="col-12 col-lg-4"><div class="card shadow-sm h-100"><div class="card-header py-2 small fw-semibold">Device split</div><div class="card-body"><canvas id="ua-device"></canvas></div></div></div>
    </div>`;

  const rangeEl = body.querySelector('#ua-range');
  const load = async (days) => {
    rangeEl.querySelectorAll('button').forEach(b => b.classList.toggle('btn-primary', +b.dataset.days === days) || b.classList.toggle('btn-outline-secondary', +b.dataset.days !== days));
    destroyCharts();
    let d; try { d = await loadOverview(days); } catch { return; }
    body.querySelector('#ua-totals').textContent = `${d.totals.pageviews} pageviews · ${d.totals.uniqueUsers} unique users`;
    const labels = d.perDay.map(x => x.day.slice(5));
    charts.push(new Chart(body.querySelector('#ua-users'), { type: 'line', data: { labels, datasets: [{ label: 'Users', data: d.perDay.map(x => x.users), borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,.1)', fill: true, tension: .3 }] }, options: chartOpts() }));
    charts.push(new Chart(body.querySelector('#ua-views'), { type: 'bar', data: { labels, datasets: [{ label: 'Pageviews', data: d.perDay.map(x => x.pageviews), backgroundColor: '#0d6efd' }] }, options: chartOpts() }));
    const tp = d.topPages.slice(0, 10);
    charts.push(new Chart(body.querySelector('#ua-pages'), { type: 'bar', data: { labels: tp.map(p => pageLabel(p.page)), datasets: [{ label: 'Views', data: tp.map(p => p.views), backgroundColor: '#198754' }] }, options: { ...chartOpts(), indexAxis: 'y' } }));
    charts.push(new Chart(body.querySelector('#ua-device'), { type: 'doughnut', data: { labels: ['Desktop', 'Mobile'], datasets: [{ data: [d.deviceCount.desktop, d.deviceCount.mobile], backgroundColor: ['#2563eb', '#f59e0b'] }] }, options: { responsive: true, maintainAspectRatio: false } }));
  };
  rangeEl.querySelectorAll('button').forEach(b => b.addEventListener('click', () => load(+b.dataset.days)));
  await load(30);
}

function chartOpts() {
  return { responsive: true, maintainAspectRatio: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } };
}

// ---------------- PER-USER DRILLDOWN ----------------
async function openUserHistory(userId, name) {
  document.getElementById('uaUserModal')?.remove();
  const html = `
    <div class="modal fade" id="uaUserModal" tabindex="-1"><div class="modal-dialog modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header"><h5 class="modal-title">Recent activity — ${esc(name)}</h5><button class="btn-close" data-bs-dismiss="modal"></button></div>
        <div class="modal-body"><div id="ua-hist"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div></div>
      </div></div></div>`;
  document.body.insertAdjacentHTML('beforeend', html);
  const el = document.getElementById('uaUserModal');
  const modal = new bootstrap.Modal(el);
  el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });
  modal.show();

  let events; try { events = await loadUserHistory(userId); } catch { el.querySelector('#ua-hist').innerHTML = '<div class="text-danger small">Failed to load.</div>'; return; }
  const host = el.querySelector('#ua-hist');
  host.innerHTML = events.length ? `
    <table class="table table-sm align-middle mb-0">
      <thead class="table-light"><tr><th>Page</th><th class="text-end">Time on page</th><th class="text-end">When</th></tr></thead>
      <tbody>${events.map(e => `<tr><td>${esc(pageLabel(e.page))}</td><td class="text-end">${fmtDur(e.durationMs)}</td><td class="text-end text-muted small">${new Date(e.at).toLocaleString(navigator.language)}</td></tr>`).join('')}</tbody>
    </table>` : '<div class="text-muted small">No history yet.</div>';
}
